import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../../util/reactIntl';

import { ExternalLink, NamedLink } from '../../../../../components';

import css from './PriorityLinks.module.css';
import { createSlug } from '../../../../../util/urlHelpers';
import { DETAILS } from '../../../../EditListingPage/EditListingWizard/EditListingWizardTab';
import { EDIT, NEW } from '../../../../../util/constants';
import { draftId, draftSlug } from '../../../../../routing/routeConfiguration';

/**
 * Create component that shows only a single "Post a new listing" link.
 *
 * @param {*} props contains customLinksMenuClass
 * @returns div with only one link inside.
 */
export const CreateListingMenuLink = props => {
  return (
    <div className={props.customLinksMenuClass}>
      <NamedLink name="NewListingPage" className={classNames(css.priorityLink, css.highlight)}>
        <span className={css.priorityLinkLabel}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </NamedLink>
    </div>
  );
};
export const CreateJobListingMenuLink = props => {
  return (
    <div className={props.customLinksMenuClass}>
      <NamedLink
        name="JobPostPage"
        params={{
          slug: draftSlug,
          id: draftId,
          type: NEW,
        }}
        className={classNames(css.priorityLink, css.highlight)}
      >
        <span className={css.priorityLinkLabel}>
          <FormattedMessage id="TopbarDesktop.postJob" />
        </span>
      </NamedLink>
    </div>
  );
};
export const CreatePerksPostMenuLink = props => {
  return (
    <div className={props.customLinksMenuClass}>
      <NamedLink
        name="PerksPostPage"
        params={{
          slug: draftSlug,
          id: draftId,
          type: NEW,
        }}
        className={classNames(css.priorityLink, css.highlight)}
      >
        <span className={css.priorityLinkLabel}>
          <FormattedMessage id="TopbarDesktop.postPerk" />
        </span>
      </NamedLink>
    </div>
  );
};
/**
 * Create component that shows only a single " Edit listing" link.
 *
 * @param {*} props contains customLinksMenuClass
 * @returns div with only one link inside.
 */
export const EditListingMenuLink = props => {
  const { currentUserListing, isTopbarMobileMenuLink } = props || {};
  const currentListingId = currentUserListing?.id?.uuid;
  const currentListingTitle = currentUserListing?.attributes?.title;
  return (
    <div className={props.customLinksMenuClass}>
      <NamedLink
        name="EditListingPage"
        className={classNames(
          css.priorityLink,
          css.highlight,
          isTopbarMobileMenuLink ? css.topbarMobileMenuLink : null
        )}
        params={{
          id: currentListingId || draftId,
          slug: createSlug(currentListingTitle),
          type: EDIT,
          tab: DETAILS,
        }}
      >
        <span className={css.priorityLinkLabel}>
          <FormattedMessage id="TopbarDesktop.editListing" />
        </span>
      </NamedLink>
    </div>
  );
};

/**
 * Link component that can be used on TopbarDesktop.
 *
 * @param {*} props containing linkConfig including resolved 'route' params for NamedLink.
 * @returns NamedLink or ExternalLink component based on config.
 */
const PriorityLink = ({ linkConfig, isTopbarMobileMenuLink }) => {
  const { text, type, href, route, highlight } = linkConfig;
  const classes = classNames(css.priorityLink, {
    [css.highlight]: highlight,
    [css.topbarMobileMenuLink]: isTopbarMobileMenuLink,
  });

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    return (
      <NamedLink name={name} params={params} to={to} className={classes}>
        <span className={css.priorityLinkLabel}>{text}</span>
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={classes}>
      <span className={css.priorityLinkLabel}>{text}</span>
    </ExternalLink>
  );
};

/**
 * Create priority links, which are visible on the desktop layout on the Topbar.
 * If space is limited, this doesn't include anything to the Topbar.
 *
 * @param {*} props contains links array and setLinks function
 * @returns container div with priority links included.
 */
const PriorityLinks = props => {
  const containerRef = useRef(null);

  // With this useEffect, we measure the widths of each rendered priority link
  // This is done once before the real rendering and it's done outside the viewport.
  useEffect(() => {
    const isMeasured = props.links?.[0]?.width;
    if (containerRef.current && !isMeasured) {
      const linksFromRenderedWrapper = [...containerRef.current.childNodes];
      let cumulatedWidth = 0;
      // Generate an array of link configs with width & cumulatedWidth included
      const linksWithWidths = props.links.reduce((links, l, i) => {
        const width = linksFromRenderedWrapper[i].offsetWidth;
        cumulatedWidth = cumulatedWidth + width;
        return [...links, { ...l, width, cumulatedWidth }];
      }, []);
      props.setLinks(linksWithWidths);
    }
  }, [containerRef]);

  const { links, priorityLinks, isTopbarMobileMenuLink } = props;
  const isServer = typeof window === 'undefined';
  const isMeasured = links?.[0]?.width && (priorityLinks.length === 0 || priorityLinks?.[0]?.width);
  const styleWrapper = !!isMeasured
    ? {}
    : {
        style: {
          position: 'absolute',
          top: '-2000px',
          left: '-2000px',
          width: '100%',
          height: 'var(--topbarHeightDesktop)',
          display: 'flex',
          flexDirection: 'row',
        },
      };
  const linkConfigs = isMeasured ? priorityLinks : links;

  return isMeasured || isServer ? (
    <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
      {linkConfigs.map(linkConfig => {
        return (
          <PriorityLink
            isTopbarMobileMenuLink={isTopbarMobileMenuLink}
            key={linkConfig.text}
            linkConfig={linkConfig}
          />
        );
      })}
    </div>
  ) : (
    ReactDOM.createPortal(
      <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
        {linkConfigs.map(linkConfig => {
          return (
            <PriorityLink
              isTopbarMobileMenuLink={isTopbarMobileMenuLink}
              key={linkConfig.text}
              linkConfig={linkConfig}
            />
          );
        })}
      </div>,
      document.body
    )
  );
};

export default PriorityLinks;
